import type { GridColumnMenuItemProps } from "@mui/x-data-grid-premium";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { CalculationType } from "../../../../../../../../api/types/fieldConfigurationTypes";
import { selectTabularFields } from "../../../../store/selectors/fieldsStateSelectors";
import CalculationMenuItem from "../../../builder/common/fields/custom-field/CalculationMenuItem";
import CustomCalculationOptionsMenu from "../../../builder/common/fields/custom-field/CustomCalculationOptionsMenu";

interface Props extends GridColumnMenuItemProps {
  onSetCustomField?: (calculationType: CalculationType, fieldGuid: string, anchorEl: HTMLElement | null) => void;
  availableCalculationTypeOptions: CalculationType[];
}
const SelectCalculationTypeMenuItem = ({
  colDef,
  onClick,
  onSetCustomField,
  availableCalculationTypeOptions,
}: Props) => {
  const apiContext = useGridApiContext();

  const [calculationMenuItemAnchor, setCalculationMenuItemAnchor] = useState<HTMLElement | null>(null);
  const customCalculationOptionsMenuRef = useRef<HTMLDivElement>(null);

  const fieldsAreaValues = useSelector(selectTabularFields);

  const currentField = fieldsAreaValues.find((field) => field.config.guid === colDef?.field);

  const handleCalculationMenuItemClick = (target: HTMLElement) => {
    setCalculationMenuItemAnchor((prev) => (prev ? null : target));
  };

  const handleCalculationMenuItemClose = (event: React.MouseEvent<HTMLElement>) => {
    setCalculationMenuItemAnchor(null);
    onClick(event);
  };

  const handleCustomCalculationItemClick = (calculationType: CalculationType, event: React.MouseEvent<HTMLElement>) => {
    if (!currentField) {
      return;
    }

    onSetCustomField?.(
      calculationType,
      currentField.config.guid,
      apiContext.current.getColumnHeaderElement(colDef?.field)
    );

    setCalculationMenuItemAnchor(null);
    onClick(event);
  };

  const handleMouseLeave = (target: EventTarget | null) => {
    if (target instanceof Node && customCalculationOptionsMenuRef.current?.contains(target)) {
      return;
    }
    setCalculationMenuItemAnchor(null);
  };

  if (!colDef) {
    return null;
  }

  return (
    <>
      <CalculationMenuItem
        sx={{ "& + .MuiDivider-root": { my: 1 } }}
        onMouseEnter={setCalculationMenuItemAnchor}
        onMouseLeave={handleMouseLeave}
        onClick={handleCalculationMenuItemClick}
      />
      <CustomCalculationOptionsMenu
        ref={customCalculationOptionsMenuRef}
        selectedCalculationType={currentField?.config.calculationType ?? undefined}
        calculationMenuItemAnchor={calculationMenuItemAnchor}
        onClose={handleCalculationMenuItemClose}
        onCustomCalculationItemClick={handleCustomCalculationItemClick}
        availableCalculationTypeOptions={availableCalculationTypeOptions}
      />
    </>
  );
};

export default SelectCalculationTypeMenuItem;
